// src/pages/shipment/SKUSearchResults.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "../../utils/patchResizeObserver";
import { toast } from "../../components/AppToast";
import ConfirmDialog from "../../components/ConfirmDialog";
import ItemsTable from "../../components/ItemsTable";
import { formatToDisplayDate } from "../../utils/dateFormatter";
// ---------- utils ----------
const formatLineNo = (lineNo) => String(lineNo).padStart(4, "0");
const normalizeSoValue = (val) => {
    if (val == null) return null;
    try {
        const s = String(val).trim();
        return s === "" ? null : s;
    } catch {
        return null;
    }
};

// ---------- mappers ----------
const mapApiArrayToGroupedObject = (rawArray) => {
    const grouped = {};
    if (!Array.isArray(rawArray)) return grouped;

    rawArray.forEach((r, idx) => {
        const sku = r.skuId ?? r.SKUID ?? "UNKNOWN";

        const soCandidate =
            r.salesOrder ??
            r.soNumber ??
            (r.salesOrderId ? String(r.salesOrderId) : null) ??
            r.shipmentNumber ??
            null;
        const normalizedSO = normalizeSoValue(soCandidate);

        let soNumberFromDetails = null;
        let soDestCity = null;
        let soDestState = null;
        if (Array.isArray(r.soDetails) && r.soDetails.length > 0) {
            const first = r.soDetails[0];
            soNumberFromDetails = first?.soNumber ?? soNumberFromDetails;
            const dest = first?.destination ?? null;
            if (dest && typeof dest === "object") {
                soDestCity = dest.city ?? dest.City ?? null;
                soDestState = dest.state ?? dest.State ?? null;
            }
        }

        const coalesceQty = (...vals) =>
            vals.find(v => v !== undefined && v !== null);
        const toNumOrNull = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
        };
        const orderedQtyVal = toNumOrNull(
            coalesceQty(r.orderedQty, r.orderedQuantity, r.orderQty, r.qtyOrdered, r.expectedQty)
        );
        const expectedQtyVal = toNumOrNull(
            coalesceQty(r.expectedQty, r.orderedQty, r.orderedQuantity, r.orderQty, r.qtyOrdered)
        );



        // const expectedQtyVal = Object.prototype.hasOwnProperty.call(r, "expectedQty")
        //     ? r.expectedQty
        //     : Object.prototype.hasOwnProperty.call(r, "orderedQty")
        //         ? r.orderedQty
        //         : null;

        const mapped = {
            __originalIndex__: idx,
            lineNo: r.lineNumber ?? r.lineNo ?? null,
            purchaseOrder: r.purchaseOrder ?? null,
            purchaseOrderId: r.purchaseOrderId ?? null,
            salesOrder: normalizedSO,
            salesOrderId: r.salesOrderId ?? null,
            shipmentNumber: r.shipmentNumber ?? null,
            shipmentId: r.shipmentId ?? null,
            skuId: sku,
            item: r.item ?? r.title ?? null,
            description: r.description ?? "",
            pack: r.pack ?? "STD",
            orderedQty: orderedQtyVal,
            expectedQty: expectedQtyVal,
            openQty:
                r.openQty === undefined
                    ? orderedQtyVal == null
                        ? null
                        : orderedQtyVal
                    : r.openQty,
            dispatchQty:
                r.dispatchQty === undefined || r.dispatchQty === null ? 0 : r.dispatchQty,
            uom: r.uom ?? "EA",
            qcRequired: r.qcRequired ?? null,
            itemPOTolerance: r.itemPOTolerance ?? r.itemASNTolerance ?? r.tolerance ?? null,
            createdAt: r.createdAt ?? null,
            updatedAt: r.updatedAt ?? null,
            holdCode: r.holdCode ?? null,
            lpn: r.lpn ?? null,
            cubes: r.itemCube ?? r.cube ?? null,
            gross: r.itemWeight ?? r.grossWeight ?? null,
            net: r.netWeight ?? null,
            dateTime: r.dateTime ?? null,
            rejected: r.rejected ?? 0,

            soNumberFromDetails: soNumberFromDetails ?? null,
            soDestinationCity: soDestCity ?? null,
            soDestinationState: soDestState ?? null,
            soDetails: Array.isArray(r.soDetails) ? r.soDetails : null,

            deliveryState: r.deliveryState ?? null,
            _derived: r._derived ?? null,
        };

        if (!grouped[sku]) grouped[sku] = [];
        grouped[sku].push(mapped);
    });

    grouped.__lastAddedTable = Object.keys(grouped).slice(-1)[0] || undefined;
    return grouped;
};

const normalizeGroupedObject = (groupedObj) => {
    const out = {};
    Object.entries(groupedObj).forEach(([k, v]) => {
        if (k === "__lastAddedTable") {
            out[k] = v;
            return;
        }
        if (!Array.isArray(v)) {
            out[k] = v;
            return;
        }

        out[k] = v.map((row) => {
            const existingSo = normalizeSoValue(row.salesOrder ?? null);
            const salesOrder =
                existingSo ??
                normalizeSoValue(
                    row.soNumber ?? row.salesOrderId ?? row.shipmentNumber ?? null
                );

            let soDestinationCity = row.soDestinationCity ?? null;
            let soDestinationState = row.soDestinationState ?? null;
            if (
                (!soDestinationCity || !soDestinationState) &&
                Array.isArray(row.soDetails) &&
                row.soDetails.length > 0
            ) {
                const f = row.soDetails[0];
                const dest = f?.destination ?? null;
                if (dest && typeof dest === "object") {
                    soDestinationCity =
                        soDestinationCity ?? (dest.city ?? dest.City ?? null);
                    soDestinationState =
                        soDestinationState ?? (dest.state ?? dest.State ?? null);
                }
            }

            return { ...row, salesOrder, soDestinationCity, soDestinationState };
        });
    });

    if (!out.__lastAddedTable) {
        out.__lastAddedTable = Object.keys(out).filter(
            (k) => k !== "__lastAddedTable"
        )[0];
    }
    return out;
};

const extractRowsFromShipmentObject = (shipmentObj) => {
    if (!shipmentObj || typeof shipmentObj !== "object") return [];
    const shipmentNumber =
        shipmentObj.shipmentNumber ??
        shipmentObj.shipmentDetails?.shipmentNumber ??
        null;
    const shipmentId =
        shipmentObj._id ?? shipmentObj.shipmentDetails?._id ?? null;

    const lines = Array.isArray(shipmentObj.lineItems)
        ? shipmentObj.lineItems
        : [];
    return lines.map((li) => {



        const coalesceQty = (...vals) =>
            vals.find(v => v !== undefined && v !== null);
        const toNumOrNull = (v) => {
            const n = Number(v);
            return Number.isFinite(n) ? n : null;
        };

        let soNum = li.salesOrder ?? li.soNumber ?? null;
        let soDestCity = null;
        let soDestState = null;
        if (Array.isArray(li.soDetails) && li.soDetails.length > 0) {
            const f = li.soDetails[0];
            soNum = soNum ?? f.soNumber ?? null;
            const dest = f?.destination ?? null;
            if (dest && typeof dest === "object") {
                soDestCity = dest.city ?? dest.City ?? null;
                soDestState = dest.state ?? dest.State ?? null;
            }
        }

        const merged = {
            ...li,
            shipmentNumber: li.shipmentNumber ?? shipmentNumber,
            shipmentId: li.shipmentId ?? shipmentId,
            salesOrder: soNum ?? li.salesOrder ?? null,
            soDestinationCity: soDestCity,
            soDestinationState: soDestState,
            soDetails: Array.isArray(li.soDetails) ? li.soDetails : null,
            orderedQty: toNumOrNull(coalesceQty(li.orderedQty, li.orderedQuantity, li.orderQty, li.qtyOrdered, li.expectedQty)),
            expectedQty: toNumOrNull(coalesceQty(li.expectedQty, li.orderedQty, li.orderedQuantity, li.orderQty, li.qtyOrdered)),
            openQty: toNumOrNull(coalesceQty(li.openQty, li.orderedQty, li.expectedQty)),
        };
        return merged;
    });
};

// ---------------- Component ----------------
const SKUSearchResults = ({
    searchResults,
    onDispatchQtyChange,
    onRemoveSku,
    onSelectionChange,
    readOnly = false,
    showGRExtraColumns = false, // kept for API compatibility
    onConfirmDelivery,
    showConfirmButton = false,
    createUsing = null,
    onRemoveRow = null, // Handler for removing individual rows
}) => {
    const userInteractingRef = useRef(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteTargetGroup, setDeleteTargetGroup] = useState(null);
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
    const [bulkDeleteTargets, setBulkDeleteTargets] = useState([]);
    const [locallyRemoved, setLocallyRemoved] = useState(new Set());
    const [confirmedGroups, setConfirmedGroups] = useState(new Set());

    // POD modal state (kept for future if needed)
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmModalGroupKey, setConfirmModalGroupKey] = useState(null);
    const [confirmModalSoSummary, setConfirmModalSoSummary] = useState(null);
    const [reportVariation, setReportVariation] = useState("");
    const [podDetailsInput, setPodDetailsInput] = useState("");
    const [podFiles, setPodFiles] = useState([]);

    // --- normalize incoming data to grouped object for ItemsTable ---
    const normalizedSearchResultsRaw = useMemo(() => {
        if (Array.isArray(searchResults))
            return mapApiArrayToGroupedObject(searchResults);
        if (searchResults && typeof searchResults === "object") {
            if (
                Array.isArray(searchResults.lineItems) &&
                searchResults.lineItems.length > 0
            ) {
                const rows = extractRowsFromShipmentObject(searchResults);
                return mapApiArrayToGroupedObject(rows);
            }
            return normalizeGroupedObject(searchResults);
        }
        return {};
    }, [searchResults]);


    const origin = useMemo(() => {
        if (searchResults && typeof searchResults === "object") {
            return searchResults.__origin || null;
        }
        return null;
    }, [searchResults]);

    // Optional: normalize createUsing if parent ne numeric diya ho
    const createUsingNorm = useMemo(() => {
        const raw = (createUsing || origin?.method || "").toString().toLowerCase();
        if (raw === "1" || raw === "po") return "po";
        if (raw === "4" || raw === "sku") return "sku";
        if (raw === "3" || raw === "4" || raw === "so") return "so";
        return raw || null;
    }, [createUsing, origin]);


    // hide locally-removed groups
    const dataForTable = useMemo(() => {
        const out = {};
        Object.entries(normalizedSearchResultsRaw || {}).forEach(([k, v]) => {
            if (k === "__lastAddedTable") return;
            if (locallyRemoved.has(k)) return;
            out[k] = v;
        });
        // NOTE: we intentionally do NOT set __lastAddedTable so ItemsTable accordion behaves normally
        return out;
    }, [normalizedSearchResultsRaw, locallyRemoved]);

    // cleanup locallyRemoved/confirmed when parent data removes keys
    useEffect(() => {
        const existingKeys = new Set(
            Object.keys(normalizedSearchResultsRaw || {}).filter(
                (k) => k !== "__lastAddedTable"
            )
        );
        setLocallyRemoved((prev) => {
            const next = new Set(prev);
            for (const k of prev) if (!existingKeys.has(k)) next.delete(k);
            return next;
        });
        setConfirmedGroups((prev) => {
            const next = new Set(prev);
            for (const g of prev) if (!existingKeys.has(g)) next.delete(g);
            return next;
        });
    }, [normalizedSearchResultsRaw]);

    // ---------- ItemsTable wiring ----------
    const COLUMNS = useMemo(
        () => [
            "lineNo",
            "salesOrder",
            "soDestinationCity",
            "soDestinationState",
            "skuId",
            "description",
            "pack",
            "uom",
            "orderedQty",
            "itemPOTolerance",
            "openQty",
            "dispatchQty",
            "actions",
        ],
        []
    );

    const COLUMN_CONFIGS = {
        lineNo: { label: "Line #", maxWidth: 110 },
        salesOrder: { label: "SO Number", maxWidth: 180 },
        soDestinationCity: { label: "Destination City", maxWidth: 180 },
        soDestinationState: { label: "Destination State", maxWidth: 180 },
        skuId: { label: "SKU ID", maxWidth: 150 },
        description: { label: "Description", minWidth: 150, maxWidth: 600 },
        pack: { label: "Pack", maxWidth: 100 },
        uom: { label: "UOM", maxWidth: 100 },
        orderedQty: { label: "Ordered Qty", maxWidth: 130 },
        itemPOTolerance: { label: "Tolerance", maxWidth: 120 },
        openQty: { label: "Open Qty", maxWidth: 120 },
        dispatchQty: { label: "Dispatch Qty", maxWidth: 130 },
        actions: { label: "Actions", maxWidth: 100 },
    };

    const FILTER_PLACEHOLDERS = {
        lineNo: "Line#",
        salesOrder: "SO",
        soDestinationCity: "City",
        soDestinationState: "State",
        skuId: "SKU",
        description: "Desc",
        pack: "Pack",
        uom: "UOM",
        orderedQty: "Ordered",
        itemPOTolerance: "Tol",
        openQty: "Open",
        dispatchQty: "Disp",
        actions: "",
    };

    const headerLabelFormatter = useMemo(() => {
        return (groupKey, content = []) => {
            // Check if this is a STOP group key (STOP_1, STOP_2, etc.)
            const stopMatch = String(groupKey ?? "").match(/^STOP_(\d+)$/);
            
            if (stopMatch) {
                // This is a stop group
                const stopNumber = stopMatch[1];
                
                // Find all unique SO numbers in this stop's table
                const uniqueSO = Array.from(
                    new Set(
                        (Array.isArray(content) ? content : [])
                            .map((r) =>
                                normalizeSoValue(
                                    r.salesOrder ??
                                    r.soNumber ??
                                    r.soNumberFromDetails ??
                                    (r.salesOrderId ? String(r.salesOrderId) : r.shipmentNumber ?? null)
                                )
                            )
                            .filter(Boolean)
                    )
                );
                
                let label = `Stop ${stopNumber}`;
                if (uniqueSO.length > 0) {
                    if (uniqueSO.length === 1) {
                        label += ` — SO: ${uniqueSO[0]}`;
                    } else {
                        label += ` — SOs: ${uniqueSO.join(", ")}`;
                    }
                }
                
                // Confirmed state suffix
                const groupAllConfirmed =
                    Boolean(content?.__allConfirmed) ||
                    (Array.isArray(content) &&
                        content.length > 0 &&
                        content.every((rr) => rr.deliveryState === "delivered"));

                const isConfirmed = groupAllConfirmed || confirmedGroups.has(groupKey);
                return isConfirmed ? `${label} — Confirmed` : label;
            }

            // Original logic for non-STOP groups
            // 1) Clean SKU (groupKey may be like "ORDER::123" etc.)
            const cleanedSku = (() => {
                try {
                    const s = String(groupKey ?? "");
                    return s.includes("::") ? s.split("::").pop() : s;
                } catch {
                    return String(groupKey ?? "");
                }
            })();

            // 2) Find a single SO number if present in this group's rows
            const uniqueSO = Array.from(
                new Set(
                    (Array.isArray(content) ? content : [])
                        .map((r) =>
                            normalizeSoValue(
                                r.salesOrder ??
                                r.soNumber ??
                                r.soNumberFromDetails ??
                                (r.salesOrderId ? String(r.salesOrderId) : r.shipmentNumber ?? null)
                            )
                        )
                        .filter(Boolean)
                )
            );
            const soLabel = uniqueSO.length === 1 ? uniqueSO[0] : null;

            // 3) Decide which label to show based on createUsing/origin
            const method = (createUsingNorm || origin?.method || "").toLowerCase();

            let label = `SO/STO: ${soLabel}`;
           
            // 4) Confirmed state suffix
            const groupAllConfirmed =
                Boolean(content?.__allConfirmed) ||
                (Array.isArray(content) &&
                    content.length > 0 &&
                    content.every((rr) => rr.deliveryState === "delivered"));

            const isConfirmed = groupAllConfirmed || confirmedGroups.has(groupKey);
            return isConfirmed ? `${label} — Confirmed` : label;
        };
    }, [createUsingNorm, origin, confirmedGroups]);



    // header actions (Confirm button in header)
    const headerActions = useCallback(
        ({ groupKey, content, readOnly }) => {
            const groupAllConfirmed =
                Boolean(content?.__allConfirmed) ||
                (Array.isArray(content) &&
                    content.length > 0 &&
                    content.every((r) => r.deliveryState === "delivered"));

            const isConfirmed = groupAllConfirmed || confirmedGroups.has(groupKey);

            if (isConfirmed) {
                return (
                    <span
                        className="badge d-flex align-items-center"
                        style={{
                            backgroundColor: "#198754",
                            color: "white",
                            padding: "0.45rem 0.6rem",
                        }}
                        title="Confirmed"
                    >
                        <i className="bi bi-check-lg me-1" /> Confirmed
                    </span>
                );
            }

            if (!showConfirmButton || typeof onConfirmDelivery !== "function" || readOnly) {
                return null;
            }

            return (
                <button
                    className="btn btn-sm btn-outline-primary"
                    title="Confirm Delivery (POD)"
                    onClick={(e) => {
                        e.stopPropagation();
                        const lines = Array.isArray(content) ? content : [];
                        const minimalRecord = {
                            shipmentDetails: {
                                shipmentNumber: lines[0]?.shipmentNumber ?? null,
                                _id: lines[0]?.shipmentId ?? null,
                            },
                            lineItems: lines,
                            _id: lines[0]?.shipmentId ?? null,
                        };
                        // open Confirm mode in parent (CreateShippingTabs) with this record
                        onConfirmDelivery?.(minimalRecord, { __openConfirmDelivery: true });
                    }}
                >
                    <i className="bi bi-arrow-right-circle me-1" /> Confirm Delivery
                </button>
            );
        },
        [confirmedGroups, onConfirmDelivery, showConfirmButton]
    );

    // cell renderer
    const cellRenderer = useMemo(() => {
        return (
            column,
            row,
            originalIndex,
            { readOnly, filterInputStyle, onCellChange, onInputFocus, onInputBlur, groupKey }
        ) => {
            const lineNumberForDisplay = formatLineNo(
                row.lineNo ??
                (row.__originalIndex__ != null
                    ? row.__originalIndex__ + 1
                    : originalIndex + 1)
            );
            const shownSO =
                row.salesOrder ??
                row.soNumberFromDetails ??
                (row.salesOrderId ? String(row.salesOrderId) : row.shipmentNumber ?? "-");
            const visibleSO = normalizeSoValue(shownSO) ?? "-";

            let destCity = row.soDestinationCity ?? null;
            let destState = row.soDestinationState ?? null;
            if (
                (!destCity || !destState) &&
                Array.isArray(row.soDetails) &&
                row.soDetails.length > 0
            ) {
                const sd = row.soDetails[0]?.destination ?? {};
                destCity = destCity ?? (sd?.city ?? sd?.City ?? null);
                destState = destState ?? (sd?.state ?? sd?.State ?? null);
            }

            const display = {
                lineNo: lineNumberForDisplay,
                salesOrder: visibleSO,
                soDestinationCity: destCity ?? "-",
                soDestinationState: destState ?? "-",
                skuId: row.skuId ?? "-",
                description: row.description ?? "-",
                pack: row.pack ?? "-",
                uom: row.uom ?? "EA",
               

                 orderedQty:
               (row.orderedQty ?? row.expectedQty ?? row.openQty) == null
                     ? "-"
                 : (row.orderedQty ?? row.expectedQty ?? row.openQty),
                itemPOTolerance: row.itemPOTolerance == null ? 0 : row.itemPOTolerance,
                openQty: row.openQty == null ? "-" : row.openQty,
            };

            if (column === "dispatchQty") {
                const valStr =
                    row.dispatchQty == null || row.dispatchQty === 0
                        ? ""
                        : String(row.dispatchQty);
                return (
                    <input
                        type="number"
                        className="form-control form-control-sm"
                        style={filterInputStyle}
                        value={valStr}
                        onChange={(e) => {
                            if (readOnly) return;
                            const v = e.target.value.trim();
                            let numericValue = v === "" ? 0 : Math.max(0, parseInt(v, 10) || 0);

                            // determine order qty (prefer orderedQty / expectedQty / orderQty)
                            const orderQty = Number(
                                row.orderedQty ?? row.expectedQty ?? row.orderQty ?? row.qtyOrdered ?? 0
                            ) || 0;

                            // tolerance can be named itemPOTolerance, itemASNTolerance, tolerance, etc.
                            const tolerancePercent = Number(
                                row.itemPOTolerance ?? row.itemASNTolerance ?? row.tolerance ?? row.tolerence ?? 0
                            ) || 0;
                                const dispatchedQty = Number(row.shipmentQty ?? 0);
                            let  maxAllowed = Number((orderQty + (orderQty * tolerancePercent) / 100).toFixed(6));
                                maxAllowed -= dispatchedQty;
                            if (orderQty > 0 && numericValue > maxAllowed) {
                                numericValue = maxAllowed;

                                toast.warn(
                                    // `Dispatch limited by tolerance: max allowed ${maxAllowed} (order ${orderQty} + ${tolerancePercent}%)`,
                                    `Total Dispatch Qty exceeds Sales order tolerance`,
                                    { autoClose: 3000 }
                                );
                            }

                            onCellChange("dispatchQty", numericValue);
                        }}
                         onFocus={onInputFocus}
                         onBlur={onInputBlur}
                         disabled={readOnly}
                         placeholder="Enter qty"
                     />
                 );
             }

            if (column === "actions") {
                if (readOnly || !onRemoveRow) return null;
                return (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemoveRow(groupKey, originalIndex);
                        }}
                        title="Delete this row"
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                );
            }

            return display[column];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onRemoveRow]);

    // onCellChange bridge
    const handleCellChange = (groupKey, originalIndex, column, value) => {
        userInteractingRef.current = true;
        if (column === "dispatchQty") {
            onDispatchQtyChange?.(groupKey, originalIndex, value);
        }
        setTimeout(() => {
            userInteractingRef.current = false;
        }, 500);
    };

    // filter function
    const filterFunction = useMemo(() => {
        return (row, _originalIndex, filters) => {
            const shownSO =
                row.salesOrder ??
                row.soNumberFromDetails ??
                (row.salesOrderId ? String(row.salesOrderId) : row.shipmentNumber ?? "");
            const visibleSO = normalizeSoValue(shownSO) ?? "";

            let destCity = row.soDestinationCity ?? null;
            let destState = row.soDestinationState ?? null;
            if (
                (!destCity || !destState) &&
                Array.isArray(row.soDetails) &&
                row.soDetails.length > 0
            ) {
                const sd = row.soDetails[0]?.destination ?? {};
                destCity = destCity ?? (sd?.city ?? sd?.City ?? null);
                destState = destState ?? (sd?.state ?? sd?.State ?? null);
            }

            const derived = {
                lineNo: row.lineNo ?? row.__originalIndex__ ?? "",
                salesOrder: visibleSO,
                soDestinationCity: destCity ?? "",
                soDestinationState: destState ?? "",
                skuId: row.skuId ?? "",
                description: row.description ?? "",
                pack: row.pack ?? "",
                uom: row.uom ?? "",
                orderedQty: row.orderedQty ?? row.expectedQty ?? "",
                itemPOTolerance: row.itemPOTolerance ?? "",
                openQty: row.openQty ?? "",
                dispatchQty: row.dispatchQty ?? "",
            };

            return Object.entries(filters).every(([k, filterVal]) => {
                if (!filterVal) return true;
                const val = derived[k];
                return String(val ?? "")
                    .toLowerCase()
                    .includes(String(filterVal).toLowerCase());
            });
        };
    }, []);

    // selection snapshot for bulk delete
    const [selectedSnapshot, setSelectedSnapshot] = useState({});
    const handleSelectionChange = (selectedMap) => {
        setSelectedSnapshot(selectedMap || {});
        onSelectionChange?.(selectedMap);
    };

    // header action helpers (confirm modal + delete)
    const openConfirmModalForGroup = (groupKey, soSummary = null) => {
        setConfirmModalGroupKey(groupKey);
        setConfirmModalSoSummary(
            soSummary
                ? Array.isArray(soSummary)
                    ? soSummary.join(", ")
                    : String(soSummary)
                : null
        );
        setReportVariation("");
        setPodDetailsInput("");
        setPodFiles([]);
        setConfirmModalOpen(true);
    };
    const closeConfirmModal = () => {
        setConfirmModalOpen(false);
        setConfirmModalGroupKey(null);
        setConfirmModalSoSummary(null);
        setReportVariation("");
        setPodDetailsInput("");
        setPodFiles([]);
    };
    const handlePodFilesChange = (e) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        setPodFiles(files);
    };
    const handleConfirm = async () => {
        if (!reportVariation || String(reportVariation).trim() === "") {
            toast.error("Select a Report Variation (required).", {
                position: "top-center",
            });
            return;
        }
        if (!podFiles || podFiles.length === 0) {
            toast.error("Upload at least one POD file (required).", {
                position: "top-center",
            });
            return;
        }
        const payload = {
            reportVariation: String(reportVariation),
            receiverName: null,
            receiverContact: null,
            deliveredAt: null,
            podDetails: podDetailsInput || null,
            podUpload: podFiles.length ? podFiles : null,
        };
        const groupKey = confirmModalGroupKey;

        if (typeof onConfirmDelivery === "function") {
            try {
                const res = onConfirmDelivery(groupKey, payload);
                if (res && typeof res.then === "function") {
                    await res;
                }
                setConfirmedGroups((prev) => {
                    const next = new Set(prev);
                    next.add(groupKey);
                    return next;
                });
                toast.success("Delivery confirmed.", {
                    position: "top-center",
                    autoClose: 1200,
                });
            } catch (err) {
                console.error("onConfirmDelivery handler error:", err);
                toast.error("Failed to perform confirm action.", {
                    position: "top-center",
                });
            }
        } else {
            setConfirmedGroups((prev) => {
                const next = new Set(prev);
                next.add(groupKey);
                return next;
            });
            toast.info("Marked as confirmed locally (no handler provided).", {
                position: "top-center",
                autoClose: 1200,
            });
            console.info("onConfirmDelivery not provided. Payload:", groupKey, payload);
        }

        closeConfirmModal();
    };

    // delete (single)
    const handleRemoveGroup = (groupKey) => {
        setDeleteTargetGroup(groupKey);
        setDeleteConfirmOpen(true);
    };
    const handleConfirmSingleDelete = () => {
        setDeleteConfirmOpen(false);
        const gk = deleteTargetGroup;
        if (!gk) return;

        setLocallyRemoved((prev) => {
            const next = new Set(prev);
            next.add(gk);
            return next;
        });

        if (typeof onRemoveSku !== "function") {
            toast.warning("Delete handler not available; item removed locally.", {
                position: "top-center",
            });
            setDeleteTargetGroup(null);
            return;
        }
        try {
            onRemoveSku(gk);
            toast.success(`Delete requested for ${gk}`, {
                position: "top-center",
                autoClose: 1000,
            });
        } catch (err) {
            console.error("[SKUSearchResults] onRemoveSku error (single):", err);
            toast.error("Failed to request delete.", { position: "top-center" });
        }
        setDeleteTargetGroup(null);
    };
    // bulk delete (top bar)
    const anyGroupHasSelectedRows = useMemo(() => {
        return Object.keys(selectedSnapshot).some(
            (gk) => (selectedSnapshot[gk]?.length || 0) > 0
        );
    }, [selectedSnapshot]);

    const handleDeleteSelectedGroupsClick = () => {
        const groupsToDelete = Object.keys(selectedSnapshot).filter(
            (gk) => (selectedSnapshot[gk]?.length || 0) > 0
        );
        if (groupsToDelete.length === 0) {
            toast.info("No rows selected to delete.", { position: "top-center" });
            return;
        }
        setBulkDeleteTargets(groupsToDelete);
        setBulkDeleteConfirmOpen(true);
    };

    const handleConfirmBulkDelete = () => {
        setBulkDeleteConfirmOpen(false);
        if (!bulkDeleteTargets || bulkDeleteTargets.length === 0) {
            toast.info("Nothing to delete.", { position: "top-center" });
            return;
        }

        setLocallyRemoved((prev) => {
            const next = new Set(prev);
            bulkDeleteTargets.forEach((k) => next.add(k));
            return next;
        });

        if (typeof onRemoveSku !== "function") {
            toast.warning("Delete handler not available; items removed locally.", {
                position: "top-center",
            });
            setBulkDeleteTargets([]);
            return;
        }

        bulkDeleteTargets.forEach((gk) => {
            try {
                onRemoveSku(gk);
                toast.success(`Delete requested for ${gk}`, {
                    position: "top-center",
                    autoClose: 1000,
                });
            } catch (err) {
                console.error("[SKUSearchResults] onRemoveSku error for bulk:", err);
                toast.error(`Failed to request delete for ${gk}`, {
                    position: "top-center",
                });
            }
        });

        setBulkDeleteTargets([]);
    };

    // inject group content for potential per-group computations
    const dataWithGroupContent = useMemo(() => {
        const out = {};
        Object.entries(dataForTable || {}).forEach(([k, v]) => {
            if (!Array.isArray(v)) return;
            const withGroup = v.map((r) => ({ ...r, __groupContent__: v }));
            out[k] = withGroup;
        });
        return out;
    }, [dataForTable]);

    // Make sure we never pass __lastAddedTable to ItemsTable (fixes accordion arrows)
    const dataForItemsTable = useMemo(() => {
        const clone = { ...dataWithGroupContent };
        if ("__lastAddedTable" in clone) delete clone.__lastAddedTable;
        return clone;
    }, [dataWithGroupContent]);

    return (
        <div className="sku-search-results mt-4">
            {/* Create Using badge */}
            {createUsing && (
                <div className="mb-2">
                    <span className="badge rounded-pill text-bg-info">

                    </span>
                </div>
            )}



            {/* top bar (kept empty in confirm/view) */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div />
            </div>

            <ItemsTable
                data={dataForItemsTable}
                columns={COLUMNS}
                columnConfigs={COLUMN_CONFIGS}
                filterPlaceholders={FILTER_PLACEHOLDERS}
                cellRenderer={cellRenderer}
                headerLabelFormatter={headerLabelFormatter}
                headerActions={headerActions}             // header button/badge injection
                filterFunction={filterFunction}
                onCellChange={handleCellChange}
                onRemoveGroup={!readOnly ? handleRemoveGroup : null}
                onSelectionChange={handleSelectionChange}
                readOnly={readOnly}
                autoSelectOnInput={true}
            />

            {/* ConfirmDialog for single delete */}
            <ConfirmDialog
                open={deleteConfirmOpen}
                onClose={() => {
                    setDeleteConfirmOpen(false);
                    setDeleteTargetGroup(null);
                }}
                onConfirm={handleConfirmSingleDelete}
                title="Delete Item?"
                message="Do you really want to delete this item?"
                yesText="Yes"
                noText="No"
            />

            {/* ConfirmDialog for bulk delete */}
            <ConfirmDialog
                open={bulkDeleteConfirmOpen}
                onClose={() => {
                    setBulkDeleteConfirmOpen(false);
                    setBulkDeleteTargets([]);
                }}
                onConfirm={handleConfirmBulkDelete}
                title="Delete Item?"
                message={`Do you really want to delete the selected ${bulkDeleteTargets.length} group(s)?`}
                yesText="Yes"
                noText="No"
            />

            {/* Optional POD Modal (currently not used by header Confirm button) */}
            {confirmModalOpen && (
                <div className="modal-backdrop" style={{ backgroundColor: "transparent" }}>
                    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ display: "block" }}>
                        <div className="modal-dialog modal-md" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <div>
                                        <h5 className="modal-title mb-0">Confirm Delivery — Report POD Details</h5>
                                        {confirmModalSoSummary && (
                                            <div className="small text-muted">SO: {confirmModalSoSummary}</div>
                                        )}
                                        <div className="small text-muted">Group: {confirmModalGroupKey}</div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={closeConfirmModal}
                                    />
                                </div>

                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">POD Details</label>
                                        <textarea
                                            className="form-control"
                                            rows={3}
                                            value={podDetailsInput}
                                            onChange={(e) => setPodDetailsInput(e.target.value)}
                                            placeholder="Enter POD details / remarks (optional)"
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">
                                            POD Upload <span className="text-danger">*</span>
                                        </label>
                                        <input type="file" className="form-control" onChange={handlePodFilesChange} multiple />
                                        <div className="form-text">Upload at least one POD file (required).</div>
                                        {podFiles && podFiles.length > 0 && (
                                            <div className="mt-2">
                                                {podFiles.map((f, i) => (
                                                    <div key={i}>{f.name}</div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">
                                            Report Variation <span className="text-danger"></span>
                                        </label>
                                        <select
                                            className="form-select"
                                            value={reportVariation}
                                            onChange={(e) => setReportVariation(e.target.value)}
                                        >
                                            <option value="">-- Select report type --</option>
                                            <option value="Signed POD">Signed POD</option>
                                            <option value="Photo Evidence">Photo Evidence</option>
                                            <option value="ePOD">ePOD</option>
                                            <option value="GRN">GRN</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeConfirmModal}>
                                        Cancel
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={handleConfirm}>
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SKUSearchResults;


















